'use client'

import { format } from 'date-fns'
import { MessageSquare } from 'react-feather'
import { LoadingButton } from '@mui/lab'
import { useTranslations } from '@/lib/next-shim'
import { cn } from '@/lib/utils'
import { MarkdownText } from '@/shared/ui/markdown/MarkdownText'
import { useExpertiseChat } from '../model/useExpertiseChat'
import { Expertise, ExpertiseChatMessage } from '../lib/expertise-detail.types'

type ChatPanelProps = {
  expertise: Expertise
}

function ChatMessageBubble({ message, isCurrentUser }: { message: ExpertiseChatMessage; isCurrentUser: boolean }) {
  return (
    <div className={cn('flex flex-col', isCurrentUser ? 'items-end' : 'items-start')}>
      <span className="mb-1 text-xs text-gray-400">{format(new Date(message?.date || new Date()), 'PPp')}</span>
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3.5 py-2 text-sm',
          isCurrentUser ? 'rounded-br-sm bg-primary text-white' : 'rounded-bl-sm bg-gray-100 text-gray-800'
        )}
      >
        <MarkdownText>{message?.content ?? ''}</MarkdownText>
      </div>
    </div>
  )
}

export function ChatPanel({ expertise }: ChatPanelProps) {
  const t = useTranslations()
  const { messages, currentUserId, canSendMessage, newMessage, setNewMessage, isSending, sendMessage } = useExpertiseChat(expertise)

  return (
    <div className="flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-500">
        <MessageSquare size={16} aria-hidden="true" />
        {t('account.expertises.chat')}
      </h2>

      <div className="flex h-[300px] flex-col sm:h-[420px]">
        <div className="mb-4 flex-1 space-y-4 overflow-y-auto pr-1">
          {messages.map((message, index) => (
            <ChatMessageBubble key={index} message={message} isCurrentUser={message?.author?._id === currentUserId} />
          ))}
          {!messages.length && <p className="py-8 text-center text-sm text-gray-400">{t('base.noData')}</p>}
        </div>

        {canSendMessage && (
          <div className="flex gap-2 border-t border-gray-100 pt-4">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={t('account.expertises.typeMessage')}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={isSending}
            />
            <LoadingButton disableElevation loading={isSending} onClick={sendMessage} disabled={!newMessage.trim() || isSending} variant="contained">
              {isSending ? t('account.expertises.sending') : t('account.expertises.send')}
            </LoadingButton>
          </div>
        )}
      </div>
    </div>
  )
}
